import { RpcSession, type RpcStub } from 'capnweb';
import { EventEmitter } from 'events';

import * as api from '@gitbook/api-client';
import { getContentAgentsEndpoint } from '@gitbook/env';
import { reportException } from '@gitbook/util-debug';
import { assertNever } from '@gitbook/util-ts';
import { WebSocket, type WebSocketOptions, WebSocketState } from '@gitbook/util-websocket';
import {
    type AgentRpc,
    type AgentScopeOptions,
    createSocketTransport,
    getAgentRejectionStatusCode,
    isFinalAgentRejection,
    type RpcSocketTransport,
} from '@services/content-agents/browser';

export enum GitBookAgentClientStatus {
    CONNECTING = 'connecting',
    IDLE = 'idle',
    PROCESSING = 'processing',
    ERRORED = 'errored',
    DISCONNECTED = 'disconnected',
    /** The agent refused the connection; see {@link GitBookAgentClientState.rejection}. */
    REJECTED = 'rejected',
}

/**
 * Why the agent refused a connection: the HTTP status it answered the handshake with, and its
 * reason.
 */
export type GitBookAgentRejection = {
    statusCode: number;
    reason: string;
};

/**
 * Delivery status of a message in the outbox:
 * - `queued`: waiting for a connection, sent automatically on the next one.
 * - `sending`: handed to the agent, waiting for its acknowledgment.
 * - `failed`: the delivery failed or its outcome is unknown, waiting for a manual retry.
 */
export type GitBookAgentOutboxMessageStatus = 'queued' | 'sending' | 'failed';

/**
 * A message posted by the user that the agent has not acknowledged yet. Kept (and persisted)
 * until it is acknowledged or explicitly discarded, so a connection failure never silently
 * loses what the user typed.
 */
export type GitBookAgentOutboxMessage = {
    id: string;
    message: api.AIMessageInput;
    status: GitBookAgentOutboxMessageStatus;
    /** Unix timestamp (ms) of the submission, used to expire stale persisted entries. */
    createdAt: number;
};

export type GitBookAgentClientState = {
    status: GitBookAgentClientStatus;
    suggestions: string[];
    messages: api.AIMessage[];
    /** Messages accepted by the agent but held for the current response to finish. */
    queue: api.AgentQueuedMessage[];
    /** Messages posted by the user and not yet acknowledged by the agent. */
    outbox: GitBookAgentOutboxMessage[];
    /** What the agent is currently doing while processing, used for the live activity indicator. */
    activity: api.AgentActivity | null;
    /**
     * A question the agent is waiting for the user to answer. While set, the client replaces the
     * prompt with a choice control; answering it (an option or a custom message) resumes the agent.
     */
    pendingQuestion: api.AgentPendingQuestion | null;
    /**
     * Where the agent edits content: a change request, or a space edited live. Null until the agent
     * has something attached, and from an agent that predates reporting it.
     */
    editTarget: api.AgentEditTarget | null;
    /**
     * How far the conversation should reach when it changes content. Null until the conversation
     * settles on a scope (or for agents without edit scopes).
     */
    editScope: api.AgentEditScope | null;
    /**
     * Whether the conversation can only be read: a transcript the agent produced on its own,
     * published for its requester to consult. The composer is not rendered, and the agent refuses
     * a message sent to it anyway.
     */
    consultOnly: boolean;
    /**
     * The background agents this conversation has started, oldest first. Each one's live status is
     * rendered against the tool call that started it.
     */
    backgroundAgents: api.AgentBackgroundAgent[];
    /**
     * Set when the agent refused the connection rather than lost it. Final for this client — the
     * same credentials would be refused again — so the socket is not reopened on its own;
     * {@link GitBookAgentClient.reconnect} asks once more.
     */
    rejection: GitBookAgentRejection | null;
    /**
     * Whether the agent has sent this client the conversation at least once. A connection opens
     * before the agent has sent what it holds, so until then an empty `messages` says nothing about
     * the conversation — only that it has not arrived yet.
     */
    synced: boolean;
    /**
     * Whether the agent has taken a message it has not yet put in the conversation: the delivery
     * was acknowledged, and the broadcast appending the message is still on its way — the
     * acknowledgment only schedules the job that does it. Set on the acknowledgment and cleared by
     * the next `messages` event, so the conversation never reads as empty in between.
     */
    awaitingMessages: boolean;
};

/**
 * Stable state for a not-yet-created agent (e.g. a disabled connection). Used as the snapshot when
 * subscribing to a `null` agent so consumers read a consistent, disconnected state.
 */
export const DISCONNECTED_AGENT_STATE: GitBookAgentClientState = {
    status: GitBookAgentClientStatus.DISCONNECTED,
    suggestions: [],
    messages: [],
    queue: [],
    outbox: [],
    activity: null,
    pendingQuestion: null,
    editTarget: null,
    editScope: null,
    consultOnly: false,
    backgroundAgents: [],
    rejection: null,
    synced: false,
    awaitingMessages: false,
};

/**
 * How long to wait for the agent to acknowledge a posted message before flagging its
 * delivery as failed.
 */
const OUTBOX_DELIVERY_TIMEOUT_MS = 15 * 1000;

/**
 * How long {@link GitBookAgentClient.whenConnected} waits for the connection. Longer than the
 * socket's own connect timeout, so a first attempt that times out still gets a retry in.
 */
const CONNECTION_WAIT_TIMEOUT_MS = 30 * 1000;

/**
 * Persisted outbox entries older than this are dropped on restore: the conversation has
 * likely moved on and resurfacing them would confuse more than help.
 */
const OUTBOX_TTL_MS = 24 * 60 * 60 * 1000;

const OUTBOX_STORAGE_PREFIX = 'gitbook:agent:outbox:';

/**
 * WebSocket close code (1012, service restart) the server sends when it reset a connection
 * whose RPC session expired. It guarantees no in-flight frame was processed, so pending
 * deliveries can safely be requeued instead of requiring a manual retry.
 */
const SESSION_EXPIRED_CLOSE_CODE = 1012;

export type GitBookAgentClientOptions = {
    pathname: string;

    /**
     * Get a token to authenticate to GitBook Agent.
     */
    getToken: () => Promise<string>;

    /**
     * Run beside each connection's handshake: message deliveries wait on it, the socket does not.
     */
    prepareConnection?: (connection: { transcript: boolean }) => Promise<unknown>;

    /**
     * Whether to receive the conversation. Defaults to true.
     *
     * False opens the connection as a watcher: the agent sends the state it needs to report what
     * the agent is doing, and neither the conversation nor the message bodies the state would
     * otherwise carry. It is the mode the connection *starts* in, not a restriction on the client
     * — {@link GitBookAgentClient.ensureTranscript} promotes a watcher the moment something needs
     * the conversation, and nothing ever demotes one.
     */
    transcript?: boolean;

    /**
     * Override pieces of the underlying socket configuration. Intended for tests, which
     * inject a scriptable socket implementation.
     */
    webSocketOptions?: Partial<WebSocketOptions>;
};

/**
 * Client for GitBook Agent content service.
 */
export class GitBookAgentClient<Rpc extends {} = any> {
    private ws: WebSocket;
    private events = new EventEmitter();
    private rpcTransport!: RpcSocketTransport;

    // @ts-expect-error - Serializable is not exported from capnweb
    private rpcSession!: RpcSession<Rpc & AgentRpc>;
    // @ts-expect-error - Serializable is not exported from capnweb
    private rpc!: RpcStub<Rpc & AgentRpc>;

    /**
     * True when the current RPC session can no longer be used (its connection died, or the
     * server aborted it) and must be rebuilt for the next connection.
     */
    private rpcSessionBroken = false;

    private agentState: api.AgentState = {
        status: api.AgentStatus.Idle,
        suggestions: [],
        queuedMessages: [],
        queue: [],
    };

    public state: GitBookAgentClientState = {
        status: GitBookAgentClientStatus.CONNECTING,
        suggestions: [],
        messages: [],
        queue: [],
        outbox: [],
        activity: null,
        pendingQuestion: null,
        editTarget: null,
        editScope: null,
        consultOnly: false,
        backgroundAgents: [],
        rejection: null,
        synced: false,
        awaitingMessages: false,
    };

    /** Pathname of the agent, also identifying its persisted outbox. */
    private pathname: string;

    /** Whether this connection receives the conversation, or only the agent's state. */
    private transcript: boolean;

    /** The current connection's {@link GitBookAgentClientOptions.prepareConnection}, never rejecting. */
    private connectionPrepared: Promise<unknown> = Promise.resolve();

    constructor(params: GitBookAgentClientOptions) {
        this.pathname = params.pathname;
        this.transcript = params.transcript ?? true;
        this.state = {
            ...this.state,
            outbox: this.restoreOutbox(),
        };

        this.ws = new WebSocket(
            async () => {
                const url = new URL(params.pathname, getContentAgentsEndpoint());
                url.protocol = url.protocol.replace('http', 'ws');

                // Read as the connection opens, so a client promoted between two attempts asks
                // for the conversation on the next one.
                const transcript = this.transcript;
                // A failed preparation never holds a message back: the agent says so through
                // what it does with it, which the user can see and retry.
                this.connectionPrepared = Promise.resolve(
                    params.prepareConnection?.({ transcript })
                ).catch(() => {});
                const token = await params.getToken();
                url.searchParams.set('token', token);
                if (!transcript) {
                    url.searchParams.set('transcript', 'false');
                }

                return url;
            },
            {
                // The RPC session is bound to a single connection (the server pairs a fresh
                // one with each): frames from a previous session must never be replayed into
                // a new connection, dead connections must be detected so the session can be
                // rebuilt, and a connection attempt must never hang forever.
                queueMessagesWhileClosed: false,
                // Asked with the close frame itself rather than read back off the socket
                // afterwards, so a refusal is judged on the connection it ended and a later
                // transport failure is never mistaken for one.
                shouldReconnect: (close) => this.shouldReconnectAfterClose(close),
                // The default ping/pong pair, answered by the agent durable object through
                // its socket auto-response without waking it.
                heartbeat: true,
                connectTimeoutMs: 20 * 1000,
                ...params.webSocketOptions,
            }
        );

        this.setupRpcSession();

        this.ws.listen('state', (wsState) => {
            if (wsState === WebSocketState.OPEN) {
                // The server created a fresh RPC session for this connection; a client
                // session carried over from a previous connection would be out of sync with
                // it (capnweb sessions are stateful), making every call hang or abort.
                if (this.rpcSessionBroken) {
                    this.setupRpcSession();
                }
                this.flushOutbox();
            } else if (wsState === WebSocketState.CLOSED || wsState === WebSocketState.ERRORED) {
                this.failRpcSession(new Error('The connection to the agent was lost'));
            }

            this.updateState((state) => ({
                ...state,
                status: getStatus(this.ws, this.agentState, this.state.rejection),
            }));
        });

        this.ws.listen('message', (rawMessage) => {
            if (typeof rawMessage !== 'string') {
                return;
            }

            const data = JSON.parse(rawMessage) as api.AgentStream;

            switch (data.type) {
                /**
                 * The agent is responding to a RPC call.
                 */
                case 'rpc': {
                    this.rpcTransport.onMessage(
                        typeof data.message === 'string'
                            ? data.message
                            : JSON.stringify(data.message)
                    );
                    break;
                }

                /**
                 * The agent is broadcasting new messages.
                 */
                case 'messages': {
                    const outbox = this.state.outbox;
                    this.updateState((state) => {
                        if (data.operation === 'replace') {
                            state.messages = data.messages;
                            // The whole conversation, sent on every connection: from here on the
                            // client knows what it holds.
                            state.synced = true;
                        } else {
                            state.messages = [...state.messages];
                            data.messages.forEach((message) => {
                                upsertMessage(state.messages, message);
                            });
                        }
                        state.outbox = removeShownOutboxMessages(
                            state.outbox,
                            data.messages.map((message) => message.clientMessageId)
                        );
                        // Whatever the agent sends here is what the conversation holds: a message
                        // it took but had not shown yet is either in it now, or gone for good.
                        state.awaitingMessages = false;

                        return state;
                    });
                    if (this.state.outbox !== outbox) {
                        this.persistOutbox();
                    }
                    break;
                }

                case 'message_response': {
                    const response = data.response;
                    switch (response.type) {
                        /**
                         * The agent is starting a new response.
                         */
                        case 'response_start': {
                            this.updateProcessingMessage(response.messageId, (message) => {
                                message.task = response.task;
                            });
                            break;
                        }

                        /**
                         * The agent is starting a new step in a message response.
                         */
                        case 'response_step_start': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    step.phase = response.phase;
                                }
                            );
                            break;
                        }

                        /**
                         * The agent finished a response.
                         */
                        case 'response_finish': {
                            this.updateProcessingMessage(response.messageId, (message) => {
                                message.response = response.response;
                            });
                            break;
                        }

                        /**
                         * The agent is processing a tool call in a new message.
                         */
                        case 'response_tool_call': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    step.toolCalls = [...(step.toolCalls ?? []), response.toolCall];
                                }
                            );
                            break;
                        }

                        /**
                         * The agent is attaching an image to a message.
                         */
                        case 'response_attachment': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    step.attachments = [
                                        ...(step.attachments ?? []),
                                        response.attachment,
                                    ];
                                }
                            );
                            break;
                        }

                        /**
                         * The agent is compacting the context.
                         */
                        case 'response_compaction': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    step.startCondition =
                                        api.AIMessageStepStartCondition.Compaction;
                                }
                            );
                            break;
                        }

                        /**
                         * The agent is writing the content of a new message.
                         */
                        case 'response_reasoning':
                        case 'response_document': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    const container =
                                        response.type === 'response_reasoning'
                                            ? 'reasoning'
                                            : 'content';

                                    step[container] ??= {
                                        object: 'document',
                                        data: {},
                                        nodes: [],
                                    };
                                    step[container] = {
                                        ...step[container],
                                        nodes: [...step[container].nodes],
                                    };
                                    if (response.operation === 'insert') {
                                        step[container].nodes.push(...response.blocks);
                                    } else {
                                        step[container].nodes.splice(
                                            -response.blocks.length,
                                            response.blocks.length,
                                            ...response.blocks
                                        );
                                    }

                                    if (response.type === 'response_reasoning') {
                                        step.timing = {
                                            ...(step.timing ?? {}),
                                            reasoning: {
                                                start:
                                                    step.timing?.reasoning?.start ??
                                                    new Date().toISOString(),
                                                end: new Date().toISOString(),
                                            },
                                        };
                                    }
                                }
                            );
                            break;
                        }
                        case 'response_object':
                        case 'response_followup_suggestion':
                        case 'response_tool_call_pending': {
                            break;
                        }
                        default:
                            assertNever(response);
                    }
                    break;
                }

                /**
                 * The state is being updated
                 */
                case 'state': {
                    this.agentState = data.state;
                    const { outbox } = this.state;
                    this.updateState((state) => ({
                        ...state,
                        outbox: removeShownOutboxMessages(
                            state.outbox,
                            (data.state.queue ?? []).map((entry) => entry.message.clientMessageId)
                        ),
                        status: getStatus(this.ws, data.state, this.state.rejection),
                        suggestions: data.state.suggestions ?? [],
                        // Entries carry the identifiers their controls act with; a server from
                        // before they existed sends only the bare `queuedMessages`, which offer
                        // nothing to act on — an empty queue, exactly as that client had.
                        queue: data.state.queue ?? [],
                        activity: data.state.activity ?? null,
                        pendingQuestion: data.state.pendingQuestion ?? null,
                        editTarget: data.state.editTarget ?? null,
                        editScope: data.state.editScope ?? null,
                        consultOnly: data.state.consultOnly ?? false,
                        backgroundAgents: data.state.backgroundAgents ?? [],
                    }));
                    if (this.state.outbox !== outbox) {
                        this.persistOutbox();
                    }
                    break;
                }
            }
        });
    }

    /**
     * (Re)create the RPC session for the current connection. The server creates a fresh
     * session per WebSocket connection and capnweb sessions are stateful, so a session must
     * never span two connections: this runs at construction and again after every
     * reconnection.
     */
    private setupRpcSession() {
        this.rpcSessionBroken = false;
        this.rpcTransport = createSocketTransport(this.ws);
        // @ts-expect-error - Serializable is not exported from capnweb
        this.rpcSession = new RpcSession<Rpc>(this.rpcTransport);
        this.rpc = this.rpcSession.getRemoteMain();

        const stub = this.rpc;
        stub.onRpcBroken((error: unknown) => {
            if (this.rpc !== stub) {
                // A session that was already replaced: nothing to do.
                return;
            }

            this.rpcSessionBroken = true;

            if (this.ws.state === WebSocketState.OPEN) {
                // The session broke while the connection looks healthy: the server aborted
                // it (e.g. it restarted and lost the session state). Recycle the connection
                // to get a fresh session pair on both sides.
                reportException(
                    new Error(
                        `GitBook Agent RPC session broken while connected: ${
                            error instanceof Error ? error.message : String(error)
                        }`
                    )
                );
                this.ws.recycle();
            }
        });
    }

    /**
     * Mark the current RPC session as unusable and reject everything pending on it. Without
     * this, calls made over a dead connection would hang forever: the transport has no way
     * to notice the connection loss on its own.
     */
    private failRpcSession(error: Error) {
        if (this.rpcSessionBroken) {
            return;
        }

        this.rpcSessionBroken = true;
        this.rpcTransport.onError(error);
    }

    /**
     * Whether a closed connection is worth reopening.
     *
     * The socket asks this as it closes, handing over the frame that ended it — so the answer is
     * about the connection that just died. A refusal is recorded and stops the socket; everything
     * else reconnects as before, including the transport failures that produce no frame at all
     * and so never reach here.
     */
    private shouldReconnectAfterClose(close: { code: number; reason: string }): boolean {
        const statusCode = getAgentRejectionStatusCode(close.code);
        if (statusCode === null || !isFinalAgentRejection(statusCode)) {
            return true;
        }

        this.recordRejection({ statusCode, reason: close.reason });
        return false;
    }

    /**
     * Record that the agent refused the connection: nothing will reopen it until the user asks,
     * so anything waiting to go out is not waiting for a connection any more.
     */
    private recordRejection(rejection: GitBookAgentRejection) {
        this.updateState((state) => ({
            ...state,
            rejection,
            status: GitBookAgentClientStatus.REJECTED,
        }));
        this.updateOutbox((outbox) =>
            outbox.map((entry) => ({ ...entry, status: 'failed' as const }))
        );
    }

    /**
     * Ask for a refused connection again, on the user's say-so.
     */
    private resumeAfterRejection() {
        this.updateState((state) => ({
            ...state,
            rejection: null,
            status: getStatus(this.ws, this.agentState, null),
        }));
        this.ws.reconnectNow();
    }

    /**
     * Connect to the agent.
     */
    public connect() {
        // Connection failures schedule their own retry inside the socket.
        this.ws.open().catch(() => {});
    }

    /**
     * Resolve once the connection is open, so the RPC stub can be called.
     *
     * Frames sent before the socket opens are dropped rather than queued, so anything reaching for
     * {@link actions} on a client it has just created must wait here first — unlike
     * {@link postMessage}, which has an outbox that survives the wait. Rejects when the agent
     * refuses the connection, since nothing reopens it on its own, and when the wait times out.
     */
    public async whenConnected(): Promise<void> {
        if (this.state.rejection) {
            throw new Error('The agent refused the connection');
        }

        if (this.ws.state === WebSocketState.OPEN) {
            return;
        }

        await withTimeout(
            new Promise<void>((resolve, reject) => {
                const unlisten = this.listen('state', () => {
                    if (this.ws.state === WebSocketState.OPEN) {
                        unlisten();
                        resolve();
                    } else if (this.state.rejection) {
                        unlisten();
                        reject(new Error('The agent refused the connection'));
                    }
                });
            }),
            CONNECTION_WAIT_TIMEOUT_MS,
            'The connection to the agent could not be established in time'
        );
    }

    /**
     * Receive the conversation from now on, if this client was only watching the agent.
     *
     * One connection per conversation, and the mode only ever goes one way: a watcher is promoted
     * when something needs what it was not receiving, and nothing demotes it back — a demotion
     * would throw away the transcript a surface still on screen is reading. The mode is fixed at
     * the handshake, so this recycles the socket; the reconnection is immediate rather than on the
     * backoff, since a user opening a conversation is waiting on it.
     */
    public ensureTranscript() {
        if (this.transcript) {
            return;
        }

        this.transcript = true;
        this.ws.recycle();
        this.ws.reconnectNow();
    }

    /**
     * Disconnect from the agent.
     */
    public disconnect() {
        this.failRpcSession(new Error('The agent client was disconnected'));
        this.ws.destroy();
    }

    /**
     * Force an immediate reconnection to the agent, bypassing the backoff delay.
     *
     * Safe to call at any time: it is a no-op when the agent is already connected
     * or connecting. Used to recover quickly when the page regains focus after a
     * disconnection (e.g. the computer woke up or the tab was backgrounded).
     *
     * Deliberately does nothing while the agent is refusing connections: there is no outage to
     * recover from, and coming back to the tab is not the user asking to be let in again — that
     * is {@link retry}.
     */
    public reconnect() {
        if (this.state.rejection) {
            return;
        }
        this.ws.reconnectNow();
    }

    /**
     * Ask again after a refusal, and send everything the refusal stranded.
     *
     * The one way back from a refused connection: the socket does not reopen on its own, and the
     * messages that were waiting for it were failed rather than left looking like they were still
     * on their way.
     */
    public retry() {
        this.updateOutbox((outbox) =>
            outbox.map((entry) =>
                entry.status === 'failed' ? { ...entry, status: 'queued' as const } : entry
            )
        );

        if (this.state.rejection) {
            // The reconnection flushes the queue when it lands.
            this.resumeAfterRejection();
            return;
        }

        this.flushOutbox();
    }

    /**
     * Listen to events from the agent.
     */
    public listen(event: 'state', callback: (event: GitBookAgentClientState) => void): () => void {
        this.events.on(event, callback);

        return () => {
            this.events.off(event, callback);
        };
    }

    /**
     * Post a message to the agent.
     *
     * The message goes through a persisted outbox: queued while disconnected (and sent on
     * the next connection), delivered with an acknowledgment otherwise, and kept around as
     * failed — for a manual retry — when the delivery fails. A message the user typed is
     * never silently lost.
     *
     * `deliverAfter` holds back the delivery — not the message: it enters the outbox, and
     * the conversation, immediately. For a send that something must precede on the wire
     * (attaching the change request the message is about), without the user watching a
     * round-trip before their own words appear. Delivery goes ahead when the promise
     * rejects — the message matters more than whatever was meant to come before it, and
     * whoever owns that work also owns reporting its failure.
     */
    public postMessage(message: api.AIMessageInput, options?: { deliverAfter?: Promise<unknown> }) {
        // Posting into a conversation means taking part in it: a client that was only watching
        // becomes one that talks rather than refusing, and the outbox carries the message across
        // the reconnection that brings the conversation with it.
        this.ensureTranscript();

        const entry: GitBookAgentOutboxMessage = {
            id: crypto.randomUUID(),
            message,
            status: 'queued',
            createdAt: Date.now(),
        };

        this.updateOutbox((outbox) => [...outbox, entry]);
        const { deliverAfter } = options ?? {};
        if (deliverAfter) {
            void deliverAfter.catch(() => {}).then(() => this.deliverOutboxMessage(entry.id));
        } else {
            void this.deliverOutboxMessage(entry.id);
        }
    }

    /**
     * Retry the delivery of a failed outbox message.
     */
    public retryMessage(id: string) {
        this.updateOutbox((outbox) =>
            outbox.map((entry) =>
                entry.id === id && entry.status === 'failed'
                    ? { ...entry, status: 'queued' as const }
                    : entry
            )
        );
        if (this.state.rejection) {
            // Nothing delivers it while the connection is refused: retrying the message is
            // asking for the connection again, and the next one flushes the queue.
            this.resumeAfterRejection();
            return;
        }
        void this.deliverOutboxMessage(id);
    }

    /**
     * Drop an outbox message without sending it.
     */
    public discardMessage(id: string) {
        this.updateOutbox((outbox) => outbox.filter((entry) => entry.id !== id));
    }

    /**
     * Try to deliver an outbox message over the current connection. Left queued while
     * disconnected (the next connection flushes it). Marked failed when the agent does not
     * acknowledge it — except when the server signals that the connection was reset before
     * processing anything, in which case the message is requeued for the next connection.
     */
    private async deliverOutboxMessage(id: string) {
        const entry = this.state.outbox.find((message) => message.id === id);
        // Queued is the only state a delivery may start from. A `deliverAfter` gate can settle
        // long after its entry was failed — by a refusal, or by an earlier attempt — and must not
        // send a message the user has been shown as undelivered and left alone.
        if (!entry || entry.status !== 'queued') {
            return;
        }

        if (this.state.rejection) {
            // Queued means "waiting for the next connection", and there is no next connection
            // while the agent is refusing them: an entry left queued here would sit at
            // "Sending…" for good. Failed is the truthful state, and it is the one that offers
            // the retry — which asks for the connection again.
            this.failOutboxMessage(id);
            return;
        }

        if (this.ws.state !== WebSocketState.OPEN) {
            return;
        }

        this.updateOutbox((outbox) =>
            outbox.map((message) =>
                message.id === id ? { ...message, status: 'sending' as const } : message
            )
        );

        try {
            await this.connectionPrepared;
            await withTimeout(
                Promise.resolve(
                    this.rpc.postMessage({ ...entry.message, clientMessageId: entry.id })
                ),
                OUTBOX_DELIVERY_TIMEOUT_MS,
                'The agent did not acknowledge the message in time'
            );

            // Acknowledged: the agent owns the message from here on. It has not shown it yet,
            // though — the append is broadcast by a job the acknowledgment only scheduled — so the
            // conversation stays spoken for until that lands. One update for both, so no reader
            // sees the outbox empty before the flag is up.
            this.updateState((state) => ({
                ...state,
                outbox: state.outbox.filter((message) => message.id !== id),
                // Unless the agent's copy already arrived, which took it out of the outbox.
                awaitingMessages:
                    state.awaitingMessages || state.outbox.some((message) => message.id === id),
            }));
            this.persistOutbox();
        } catch (_error) {
            if (this.ws.lastClose?.code === SESSION_EXPIRED_CLOSE_CODE) {
                // The server reset the connection without processing any frame: resending
                // cannot duplicate the message.
                this.updateOutbox((outbox) =>
                    outbox.map((message) =>
                        message.id === id ? { ...message, status: 'queued' as const } : message
                    )
                );
                if (this.ws.state === WebSocketState.OPEN) {
                    // The reconnection already happened: flush now, there will be no OPEN
                    // transition to do it.
                    void this.deliverOutboxMessage(id);
                }
                return;
            }

            this.failOutboxMessage(id);

            if (!this.state.rejection) {
                // An unacknowledged delivery over a healthy-looking socket is how a half-open
                // connection or a broken session shows up: probe it so it gets recycled if dead.
                // Skipped after a refusal, which is not a sick connection but a closed door.
                this.ws.reconnectNow();
            }
        }
    }

    /**
     * Mark one outbox entry as failed, so it is shown as undelivered with its retry and discard
     * controls rather than as still on its way.
     */
    private failOutboxMessage(id: string) {
        this.updateOutbox((outbox) =>
            outbox.map((message) =>
                message.id === id ? { ...message, status: 'failed' as const } : message
            )
        );
    }

    /**
     * Send every queued outbox message. Runs when a connection comes up.
     */
    private flushOutbox() {
        for (const entry of this.state.outbox) {
            if (entry.status === 'queued') {
                void this.deliverOutboxMessage(entry.id);
            }
        }
    }

    /**
     * Update the outbox, notify listeners and persist it so a page reload can recover
     * messages the agent never acknowledged.
     */
    private updateOutbox(fn: (outbox: GitBookAgentOutboxMessage[]) => GitBookAgentOutboxMessage[]) {
        this.updateState((state) => ({
            ...state,
            outbox: fn(state.outbox),
        }));
        this.persistOutbox();
    }

    private get outboxStorageKey() {
        return `${OUTBOX_STORAGE_PREFIX}${this.pathname}`;
    }

    private persistOutbox() {
        try {
            if (this.state.outbox.length === 0) {
                localStorage.removeItem(this.outboxStorageKey);
            } else {
                localStorage.setItem(this.outboxStorageKey, JSON.stringify(this.state.outbox));
            }
        } catch (_error) {
            // Storage unavailable (server-side rendering, private mode, quota exceeded):
            // the outbox is memory-only.
        }
    }

    /**
     * Restore the outbox persisted by a previous page. Entries come back as failed —
     * requiring a manual retry — because their delivery outcome is unknown: resending them
     * automatically could duplicate a message the agent already processed.
     */
    private restoreOutbox(): GitBookAgentOutboxMessage[] {
        try {
            const raw = localStorage.getItem(this.outboxStorageKey);
            if (!raw) {
                return [];
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
                return [];
            }

            return (parsed as GitBookAgentOutboxMessage[])
                .filter(
                    (entry) =>
                        entry &&
                        typeof entry.id === 'string' &&
                        typeof entry.createdAt === 'number' &&
                        entry.message &&
                        Date.now() - entry.createdAt < OUTBOX_TTL_MS
                )
                .map((entry) => ({ ...entry, status: 'failed' as const }));
        } catch (_error) {
            return [];
        }
    }

    /**
     * Stop the current agent response if it's still running.
     */
    public async stop() {
        await this.rpc.stop();
    }

    /**
     * Run a one-off background task from a custom prompt. Resolves as soon as the task is scheduled
     * on the agent — not when it finishes — so track its progress through the agent state (the
     * agent is `PROCESSING` while it runs). Idempotent: starting a task while one is already running
     * on this connection is a no-op. Run several tasks in parallel by pointing each client at its
     * own conversation. See {@link AgentRpc.runTask}.
     */
    public async runTask(prompt: string, scopeOptions?: AgentScopeOptions): Promise<void> {
        await this.rpc.runTask(prompt, scopeOptions);
    }

    /**
     * Remove a message from the queue, returning it so the caller can put it back in the
     * composer. Rejects when the entry is gone — the agent already picked it up.
     */
    public async unqueueMessage(id: string): Promise<api.AIMessageInput> {
        // Use the wire contract before calling to avoid expanding capnweb's recursive message type.
        const rpc = this.rpc as unknown as Pick<AgentRpc, 'unqueueMessage'>;
        return rpc.unqueueMessage({ id });
    }

    /**
     * Interrupt the current response and answer the queued message right away, alone. The rest of
     * the queue keeps its place and follows once that response finishes.
     */
    public async sendQueuedMessageNow(id: string): Promise<void> {
        await this.rpc.sendQueuedMessageNow({ id });
    }

    /**
     * The methods this particular agent adds, beyond the ones every agent has.
     *
     * Typed by the client's `Rpc` parameter, so a caller holding a
     * `GitBookAgentClient<AgentOrganizationRpc>` reaches that agent's actions and nothing else,
     * without every agent-specific action having to be mirrored onto this class.
     *
     * Only usable once connected — the stub is bound when the session opens.
     */
    public get actions() {
        return this.rpc;
    }

    /**
     * Update a message in the chat.
     */
    private updateProcessingMessage(messageId: string, callback: (message: api.AIMessage) => void) {
        this.updateState((state) => {
            state.messages = [...state.messages];
            const existing = state.messages.findIndex((m) => m.id === messageId);
            if (existing !== -1) {
                state.messages[existing] = { ...state.messages[existing] };
                callback(state.messages[existing]);
            } else {
                const newMessage: api.AIMessage = {
                    id: messageId,
                    role: api.AIMessageRole.Assistant,
                    steps: [],
                };
                callback(newMessage);
                state.messages.push(newMessage);
            }

            return state;
        });
    }

    /**
     * Update a step in a message.
     */
    private updateProcessingMessageStep(
        messageId: string,
        stepIndex: number,
        callback: (step: api.AIMessageStep) => void
    ) {
        this.updateProcessingMessage(messageId, (message) => {
            if (stepIndex > message.steps.length) {
                throw new Error(
                    `Step index out of bounds ${stepIndex} for message ${messageId} (${message.steps.length} steps)`
                );
            }

            if (message.steps[stepIndex]) {
                message.steps = [...message.steps];
                message.steps[stepIndex] = { ...message.steps[stepIndex] };
                callback(message.steps[stepIndex]);
            } else {
                message.steps = [...message.steps, {}];
                callback(message.steps[stepIndex]);
            }
        });
    }

    /**
     * Update the agent state.
     */
    private updateState(fn: (previous: GitBookAgentClientState) => GitBookAgentClientState) {
        this.state = fn({
            ...this.state,
        });
        this.events.emit('state', this.state);
    }
}

/**
 * Utility to extract the latest task from the state of the agent.
 */
export function extractLatestAgentTask(state: GitBookAgentClientState): api.AIMessageTask | null {
    const latestMessage = state.messages[state.messages.length - 1];
    if (!latestMessage) {
        return null;
    }

    return latestMessage.task ?? null;
}

/**
 * Utility to extract the active task from the state of the agent.
 */
export function extractActiveAgentTask(state: GitBookAgentClientState): api.AIMessageTask | null {
    if (state.status !== GitBookAgentClientStatus.PROCESSING) {
        return null;
    }

    return extractLatestAgentTask(state);
}

/**
 * Bound a promise in time: a delivery waiting on a dead connection must eventually surface
 * as a failure instead of leaving the message in a "sending" limbo.
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(new Error(message));
        }, timeoutMs);

        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            }
        );
    });
}

/**
 * Drop the outbox entries the agent already shows, in the conversation or in its queue: its copy of
 * a message can arrive before its acknowledgment, and one that timed out may still have arrived.
 */
function removeShownOutboxMessages(
    outbox: GitBookAgentOutboxMessage[],
    shownIds: Array<string | undefined>
): GitBookAgentOutboxMessage[] {
    const shown = new Set(shownIds);
    return outbox.some((entry) => shown.has(entry.id))
        ? outbox.filter((entry) => !shown.has(entry.id))
        : outbox;
}

function upsertMessage(messages: api.AIMessage[], message: api.AIMessage) {
    const existing = messages.findIndex((m) => m.id === message.id);
    if (existing !== -1) {
        messages[existing] = message;
    } else {
        messages.push(message);
    }
}

function getStatus(
    ws: WebSocket,
    state: api.AgentState,
    rejection: GitBookAgentRejection | null
): GitBookAgentClientStatus {
    // A refused connection is not a lost one: nothing reopens it on its own.
    if (rejection) {
        return GitBookAgentClientStatus.REJECTED;
    }

    // An errored socket is as disconnected as a closed one: falling through to the
    // agent-provided status would display a stale IDLE/PROCESSING while nothing is connected.
    if (ws.state === WebSocketState.CLOSED || ws.state === WebSocketState.ERRORED) {
        return GitBookAgentClientStatus.DISCONNECTED;
    }

    if (ws.state === WebSocketState.CONNECTING) {
        return GitBookAgentClientStatus.CONNECTING;
    }

    if (state.status === api.AgentStatus.Processing) {
        return GitBookAgentClientStatus.PROCESSING;
    }

    if (state.status === api.AgentStatus.Errored) {
        return GitBookAgentClientStatus.ERRORED;
    }

    return GitBookAgentClientStatus.IDLE;
}
